import React from "react";
import { useTheme } from "styled-components";
import { ICartProduct, IStore } from "../../../../classes/interfaces";
import { formatBRL } from "../../../../helpers/currency.helper";
import Icon from "../../../../components/ui/Icon";
import Photo from "../../../../components/ui/Photo";
import Button from "../../../../components/ui/Button";
import QuantityStepper from "../../../../components/ui/QuantityStepper";
import SummaryRow from "../../../../components/ui/SummaryRow";
import { useCart } from "../../../../hooks/useCart";
import { deliveryFeeLabel, isVariableDeliveryFee } from "../../helpers/deliveryFee";
import { removeCartLine, setCartLineAmount } from "../../../../utils/cartCalculations";
import {
  Wrap,
  Header,
  HeaderIconBadge,
  HeaderText,
  Title,
  Count,
  ClearButton,
  EmptyState,
  EmptyIconBadge,
  EmptyTitle,
  EmptyHint,
  List,
  Item,
  ThumbWrap,
  ItemInfo,
  ItemName,
  ItemExtras,
  ItemObs,
  FlavourNote,
  ItemRight,
  ItemTotal,
  RemoveButton,
  Footer,
  FooterDivider,
  TotalRow,
  TotalLabel,
  TotalValue,
} from "./DesktopCartSidebar.styles";

interface DesktopCartSidebarProps {
  company: IStore;
  onCheckout: () => void;
}

export default function DesktopCartSidebar({ company, onCheckout }: DesktopCartSidebarProps) {
  const theme = useTheme();
  const { cart, setCart, clearCart } = useCart();
  const count = cart.products.reduce((sum, p) => sum + (p.amount || 0), 0);

  function removeLine(product: ICartProduct) {
    removeCartLine(cart, product);
    setCart({ ...cart });
  }

  function setLineAmount(product: ICartProduct, newAmount: number) {
    setCartLineAmount(cart, product, newAmount);
    setCart({ ...cart });
  }

  return (
    <Wrap>
      <Header>
        <HeaderIconBadge>
          <Icon name="bag" size={19} />
        </HeaderIconBadge>
        <HeaderText>
          <Title>Sua sacola</Title>
          <Count>
            {count} {count === 1 ? "item" : "itens"}
          </Count>
        </HeaderText>
        {cart.products.length > 0 && (
          <ClearButton type="button" onClick={clearCart}>
            Limpar
          </ClearButton>
        )}
      </Header>

      {cart.products.length === 0 ? (
        <EmptyState>
          <EmptyIconBadge>
            <Icon name="bag" size={32} color={theme.tokens.colors.teal} />
          </EmptyIconBadge>
          <EmptyTitle>Sacola vazia</EmptyTitle>
          <EmptyHint>Escolha itens do cardápio ao lado para começar seu pedido.</EmptyHint>
        </EmptyState>
      ) : (
        <>
          <List>
            {cart.products.map((item) => {
              const extrasSummary = item.extras.map((e) => `${e.amount}x ${e.name}`).join(" · ");
              const isFlavoured = !!item.flavours && item.flavours.length > 0;

              return (
                <Item key={item.id}>
                  <ThumbWrap>
                    <Photo src={item.imageUrl} radius={12} />
                  </ThumbWrap>
                  <ItemInfo>
                    <ItemName>{item.product}</ItemName>
                    {extrasSummary && <ItemExtras>{extrasSummary}</ItemExtras>}
                    {item.obs && <ItemObs>Obs: {item.obs}</ItemObs>}
                    {isFlavoured ? (
                      <FlavourNote>{item.flavours!.length} sabores</FlavourNote>
                    ) : (
                      <QuantityStepper
                        value={item.amount}
                        min={0}
                        onChange={(value) => setLineAmount(item, value)}
                        size="sm"
                      />
                    )}
                  </ItemInfo>
                  <ItemRight>
                    <ItemTotal>{formatBRL(item.total)}</ItemTotal>
                    {isFlavoured && (
                      <RemoveButton
                        type="button"
                        aria-label={`Remover ${item.product}`}
                        onClick={() => removeLine(item)}
                      >
                        <Icon name="trash" size={15} />
                      </RemoveButton>
                    )}
                  </ItemRight>
                </Item>
              );
            })}
          </List>

          <Footer>
            <SummaryRow label="Subtotal" value={formatBRL(cart.total)} />
            <SummaryRow label="Taxa de entrega" value={deliveryFeeLabel(company)} />
            <FooterDivider />
            <TotalRow>
              <TotalLabel>Total</TotalLabel>
              <TotalValue>
                {/* taxa variável só é conhecida no checkout — total aqui é o subtotal */}
                {formatBRL(cart.total + (isVariableDeliveryFee(company) ? 0 : company.tax || 0))}
              </TotalValue>
            </TotalRow>
            <Button variant="primary" size="lg" full onClick={onCheckout}>
              Finalizar compra
            </Button>
          </Footer>
        </>
      )}
    </Wrap>
  );
}
